<template>
    <div class="consent-bar" :class="hidingClass" data-test="consent-bar">
        <WebpImage v-if="imageVisible" :src="image" alt="cookie lišta" css-class="consent-bar__image" />
        <div class="consent-bar__content" ref="contentSection" @scroll="hideKulicnik">
            <h2 class="consent-bar__title" v-html="headerToShow"></h2>
            <div class="consent-bar__text" v-html="textToShow"></div>
        </div>
        <div class="consent-bar__actions">
            <button type="button" class="btn btn-sm btn-secondary" @click="consentAll">Souhlasím se vším</button>
            <button type="button" class="btn btn-sm consent-bar__more" @click="consentToOnlyNecessary">
                Pouze nezbytné
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted, ref, nextTick, computed, watch } from "vue";
import WebpImage from "@sazka/design-system/src/image/webp-image.vue";
import { useWecomaIntegration } from "./use-wecoma-integration";
import { pushConsentBarShow, pushAllConsentBar, pushEssentialsConsentBar } from "./use-wecoma-datalayer";
import { useBreakpoints } from "../composables/use-breakpoints";
import { WecomaConstants } from "./wecoma-constants";
import { ContactWecomaKeys } from "../models/contact";

const {
    consentToAllPurposes,
    redirectToPreferenceCenter,
    redirectToPrivacyPolicy,
    consentToOnlyNecessaryPurposes,
    getWecomaKeysValue,
    canBeLoggedInWecoma,
} = useWecomaIntegration();

const header = environment.gdpr.gdprHeader;
const headerMobile = environment.gdpr.gdprHeaderMobile || environment.gdpr.gdprHeader;
const text = environment.gdpr.gdprMessage;
const textMobile = environment.gdpr.gdprMessageMobile || environment.gdpr.gdprMessage;
const textLoggedIn = environment.gdpr.gdprMessageLoggedIn || environment.gdpr.gdprMessage;
const textMobileLoggedIn = environment.gdpr.gdprMessageMobileLoggedIn || textLoggedIn;
const image = environment.gdpr.gdprImageUrl;

const wecomaUserKeys = ref<ContactWecomaKeys>();
const isLoggedInWecoma = computed(() => !!wecomaUserKeys.value);
const isSm2Up = useBreakpoints("sm2");

const hidingClass = ref("");
const hide = () => {
    hidingClass.value = "hiding";
    setTimeout(() => {
        hidingClass.value = "hidden";
    }, 500);
};

const consentAll = () => {
    setTimeout(() => {
        consentToAllPurposes(isLoggedInWecoma.value);
        pushAllConsentBar();
    }, 0);
    hide();
};

const consentToOnlyNecessary = () => {
    consentToOnlyNecessaryPurposes(isLoggedInWecoma.value);
    pushEssentialsConsentBar();
    hide();
};

const imageVisible = ref(true);
const hideKulicnik = (e) => {
    const sm2 = useBreakpoints("sm2");
    imageVisible.value = sm2.value || e?.target?.scrollTop === 0;
};

const headerToShow = computed(() => {
    return isSm2Up.value ? header : headerMobile;
});

const textToShow = computed(() => {
    if (isLoggedInWecoma.value) {
        return isSm2Up.value ? textLoggedIn : textMobileLoggedIn;
    }

    return isSm2Up.value ? text : textMobile;
});

watch(
    textToShow,
    () => {
        nextTick(() => {
            const preferenceCenterLink = isLoggedInWecoma.value
                ? `${appSettings["Integrations:Wecoma:Domain"]}?key=${WecomaConstants.WECOMA_CODE_DEH}&locale=cs-CZ&authKeys=${wecomaUserKeys.value.wecomaUserKey}`
                : `${appSettings["Integrations:Wecoma:Domain"]}?key=${WecomaConstants.WECOMA_CODE_DEH}&locale=cs-CZ&authKeys=${WecomaConstants.WECOMA_AUTHKEY}&contexts=consentbar&source=cookiebanner`;

            const prefCenterLinkElement = document.getElementById("consent-bar-preference-center") as HTMLAnchorElement;
            if (prefCenterLinkElement) {
                prefCenterLinkElement.href = preferenceCenterLink;
                prefCenterLinkElement.addEventListener("click", () =>
                    redirectToPreferenceCenter("text", isLoggedInWecoma.value)
                );
            }

            const privacyPolicyLinkElement = document.getElementById("consent-bar-privacy-policy") as HTMLAnchorElement;
            privacyPolicyLinkElement?.addEventListener("click", () => redirectToPrivacyPolicy(isLoggedInWecoma.value));
        });
    },
    { immediate: true }
);

onMounted(async () => {
    if (canBeLoggedInWecoma()) {
        wecomaUserKeys.value = await getWecomaKeysValue();
        pushConsentBarShow();
    }
});
</script>
